/**
 * RemediationPlaybookPanel (DC-1673)
 *
 * Prescriptive Triangulation Layer 1 — the domain-level remediation playbook
 * that sits below the four PillarCards on the triangulation drill-down. Renders
 * the deduped, lowest-maturity-first playbooks for the domain's fired patterns,
 * each with a first move, owner, verify-before-acting checklist, common misread,
 * and 3–6 ordered plays. Plays are consultant-editable (Accept / Reject / Edit) —
 * accepted/edited plays flow into the existing Action Plan pipeline server-side.
 *
 * "Diagnostic, not directive" (guide §7): nothing is committed by default; the
 * consultant curates. Suspected patterns surface flagged preliminary.
 *
 * Scoped under `.tri-view` so it consumes the cream/teal token layer; no raw hex
 * (lint:cream). Data: GET /api/triangulation/domain/:domainId/playbook.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardList, AlertTriangle, Check, X, Pencil, RotateCcw, Sparkles, User, Users } from 'lucide-react';
import { useApiClient } from '../../hooks/useApiClient';
import type {
  DomainPlaybookResolution,
  PlaybookPatternFamily,
  PlayDecisionAction,
  PlayOwnerAssignment,
  ResolvedPlay,
  ResolvedPlaybook,
} from '../../types/triangulation';

interface Props {
  domainId: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: DomainPlaybookResolution }
  | { kind: 'error'; message: string };

/** DB pattern_family → the badge color family used across the tri-view. */
const FAMILY_CLASS: Record<PlaybookPatternFamily, 'good' | 'soft' | 'hard' | 'neutral'> = {
  aligned: 'good',
  soft_divergence: 'soft',
  hard_divergence: 'hard',
  neutral: 'neutral',
};

export const RemediationPlaybookPanel: React.FC<Props> = ({ domainId }) => {
  const { authFetch } = useApiClient();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [busyPlay, setBusyPlay] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [tailoring, setTailoring] = useState(false);
  const [tailorError, setTailorError] = useState<string | null>(null);
  // DC-1707: active owner assignments keyed `${pattern_name}:${play_seq}`.
  // Metadata layer — a fetch failure degrades silently to the seeded-role
  // render (spec §1.4); it must never take the playbook panel down.
  const [ownerByPlay, setOwnerByPlay] = useState<Map<string, PlayOwnerAssignment>>(new Map());

  const loadOwners = useCallback(async (): Promise<void> => {
    try {
      const res = await authFetch(`/api/triangulation/domain/${domainId}/owner-assignments`);
      if (!res.ok) return;
      const assignments = (await res.json()) as PlayOwnerAssignment[];
      const map = new Map<string, PlayOwnerAssignment>();
      for (const a of assignments) map.set(`${a.pattern_name}:${a.play_seq}`, a);
      setOwnerByPlay(map);
    } catch (err) {
      console.warn('[RemediationPlaybookPanel] owner-assignment fetch failed; showing suggested roles only.', err);
    }
  }, [authFetch, domainId]);

  const load = useCallback(async (): Promise<void> => {
    setState({ kind: 'loading' });
    try {
      const [res] = await Promise.all([
        authFetch(`/api/triangulation/domain/${domainId}/playbook`),
        loadOwners(),
      ]);
      if (!res.ok) {
        setState({ kind: 'error', message: `Couldn’t load the playbook (HTTP ${res.status}).` });
        return;
      }
      const data = (await res.json()) as DomainPlaybookResolution;
      setState({ kind: 'ready', data });
    } catch (err) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load.' });
    }
  }, [authFetch, domainId, loadOwners]);

  useEffect(() => {
    void load();
  }, [load]);

  /** POST a new decision (no active decision yet) or PATCH the existing one. */
  const setDecision = useCallback(
    async (
      play: ResolvedPlay,
      patternName: string,
      action: PlayDecisionAction,
      edits?: { title?: string; description?: string; rationale?: string },
    ): Promise<void> => {
      const key = `${patternName}:${play.seq}`;
      setBusyPlay(key);
      try {
        const payload: Record<string, unknown> = { action };
        if (action === 'edit') {
          payload.edited_title = edits?.title ?? play.title;
          payload.edited_description = edits?.description ?? play.description;
        }
        if (edits?.rationale) payload.rationale = edits.rationale;

        let res: Response;
        if (play.decision_id) {
          res = await authFetch(`/api/triangulation/play-decisions/${play.decision_id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
        } else {
          res = await authFetch(`/api/triangulation/domain/${domainId}/play-decisions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ pattern_name: patternName, play_seq: play.seq, ...payload }),
          });
        }
        if (!res.ok) {
          const text = await res.text();
          setState({ kind: 'error', message: `Couldn’t save decision: ${text || res.status}` });
          return;
        }
        setEditing(null);
        await load();
      } catch (err) {
        setState({ kind: 'error', message: err instanceof Error ? err.message : 'Save failed.' });
      } finally {
        setBusyPlay(null);
      }
    },
    [authFetch, domainId, load],
  );

  /** Revert a decision (soft-delete) — play returns to undecided + leaves the plan. */
  const revert = useCallback(
    async (play: ResolvedPlay, patternName: string): Promise<void> => {
      if (!play.decision_id) return;
      const key = `${patternName}:${play.seq}`;
      setBusyPlay(key);
      try {
        const res = await authFetch(`/api/triangulation/play-decisions/${play.decision_id}`, {
          method: 'DELETE',
        });
        if (!res.ok && res.status !== 204) {
          setState({ kind: 'error', message: `Couldn’t revert (HTTP ${res.status}).` });
          return;
        }
        await load();
      } catch (err) {
        setState({ kind: 'error', message: err instanceof Error ? err.message : 'Revert failed.' });
      } finally {
        setBusyPlay(null);
      }
    },
    [authFetch, load],
  );

  /** DC-1639 L2: run Bridge AI tailoring over the domain's fired patterns. */
  const tailor = useCallback(async (): Promise<void> => {
    setTailoring(true);
    setTailorError(null);
    try {
      const res = await authFetch(`/api/triangulation/domain/${domainId}/playbook/tailor`, {
        method: 'POST',
      });
      if (res.status === 503) {
        setTailorError('Bridge AI isn’t configured in this environment, so plays can’t be tailored.');
        return;
      }
      if (!res.ok) {
        setTailorError(`Tailoring failed (HTTP ${res.status}).`);
        return;
      }
      const data = (await res.json()) as DomainPlaybookResolution;
      setState({ kind: 'ready', data });
    } catch (err) {
      setTailorError(err instanceof Error ? err.message : 'Tailoring failed.');
    } finally {
      setTailoring(false);
    }
  }, [authFetch, domainId]);

  if (state.kind === 'loading') {
    return (
      <section className="tpb" data-testid="playbook-panel">
        <PanelHeading />
        <div className="tri-status" role="status" aria-live="polite">
          Loading remediation playbook…
        </div>
      </section>
    );
  }

  if (state.kind === 'error') {
    return (
      <section className="tpb" data-testid="playbook-panel">
        <PanelHeading />
        <div className="tri-alert" role="alert">
          <AlertTriangle size={18} aria-hidden="true" />
          <div>
            <p className="tri-alert-title">Couldn’t load the remediation playbook</p>
            <p className="tri-alert-detail">{state.message}</p>
            <button type="button" className="tri-retry" onClick={() => void load()}>
              Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  // Defensive: a malformed/empty success body must not crash the whole domain
  // view (the panel is one section among many). Missing playbooks ⇒ empty state.
  const { playbooks = [] } = state.data ?? {};

  if (playbooks.length === 0) {
    return (
      <section className="tpb" data-testid="playbook-panel">
        <PanelHeading />
        <p className="tpb-empty" data-testid="playbook-empty">
          No remediation playbook yet. A playbook appears once a pillar fires one of the eight
          triangulation patterns — gather more signal (datasets, interviews, governance documents)
          to surface a confirmed or suspected pattern.
        </p>
      </section>
    );
  }

  const acceptedCount = playbooks.reduce(
    (n, pb) => n + pb.plays.filter((p) => p.in_action_plan).length,
    0,
  );

  return (
    <section className="tpb" data-testid="playbook-panel">
      <PanelHeading />
      <p className="tpb-caption">
        {playbooks.length} {playbooks.length === 1 ? 'pattern' : 'patterns'} fired across this
        domain, ranked lowest-maturity pillar first — lead at the top. Accept the plays you’ll act on
        and they form this domain’s Remediation Action Plan; the playbook arms the readout
        conversation, it doesn’t replace it.
      </p>

      <TailorBar
        tailored={state.data.tailored}
        tailoredAt={state.data.tailored_at}
        model={state.data.tailored_model}
        busy={tailoring}
        error={tailorError}
        onTailor={tailor}
      />

      <ol className="tpb-list">
        {playbooks.map((pb) => (
          <PlaybookCard
            key={pb.pattern_name}
            playbook={pb}
            ownerByPlay={ownerByPlay}
            busyPlay={busyPlay}
            editing={editing}
            onEdit={setEditing}
            onSetDecision={setDecision}
            onRevert={revert}
          />
        ))}
      </ol>

      <ActionPlanBar domainId={domainId} acceptedCount={acceptedCount} authFetch={authFetch} />
    </section>
  );
};

interface ActionPlanBarProps {
  domainId: string;
  acceptedCount: number;
  authFetch: (url: string, init?: RequestInit) => Promise<Response>;
}

/** The lineage-integrity message — export and warning share one truth. */
const LINEAGE_UNAVAILABLE_MESSAGE =
  'The decision history behind this plan can’t be read right now, so the ' +
  'supersession lineage would be incomplete. Compass won’t publish an export ' +
  'with a silently thinned decision trail. Retry once the history is available.';

/**
 * Footer bar: the accepted plays ARE the domain's Remediation Action Plan
 * (assembled directly, no LLM consolidation). Export to PDF/HTML.
 *
 * LINEAGE CONTRACT (closeout follow-up): the bar probes the interactive plan
 * and renders a VISIBLE warning with a retry path when `lineage_status` is
 * 'unavailable'; and a 503 whose code is DECISION_LINEAGE_UNAVAILABLE shows
 * the lineage-integrity explanation — NEVER "try HTML", because HTML fails
 * identically (the export fails closed regardless of format).
 */
const ActionPlanBar: React.FC<ActionPlanBarProps> = ({ domainId, acceptedCount, authFetch }) => {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lineageUnavailable, setLineageUnavailable] = useState(false);

  const probeLineage = useCallback(async (): Promise<void> => {
    try {
      const res = await authFetch(`/api/triangulation/domain/${domainId}/action-plan`);
      if (!res.ok) return; // the probe is supplementary; the plan surface owns its own errors
      const plan = (await res.json()) as { lineage_status?: 'complete' | 'unavailable' };
      const unavailable = plan.lineage_status === 'unavailable';
      setLineageUnavailable(unavailable);
      if (!unavailable) {
        /*
         * RETRY LIFECYCLE: a successful COMPLETE probe retires the
         * lineage-specific export alert too — a failed export earlier set
         * both the warning and the alert, and clearing only the warning left
         * a stale "history can't be read" alert standing next to a healthy
         * plan. Only the lineage message is ours to clear; any other export
         * error stays.
         */
        setError((current) => (current === LINEAGE_UNAVAILABLE_MESSAGE ? null : current));
      }
    } catch {
      /* supplementary probe — never takes the bar down */
    }
  }, [authFetch, domainId]);

  useEffect(() => {
    void probeLineage();
  }, [probeLineage]);

  const exportPlan = useCallback(
    async (format: 'pdf' | 'html'): Promise<void> => {
      setExporting(true);
      setError(null);
      try {
        const res = await authFetch(
          `/api/triangulation/domain/${domainId}/action-plan/report?format=${format}`,
        );
        if (res.status === 503) {
          // Parse the standardized body: the two 503s mean OPPOSITE things.
          const body = (await res.json().catch(() => null)) as {
            error?: { code?: string };
          } | null;
          if (body?.error?.code === 'DECISION_LINEAGE_UNAVAILABLE') {
            // Fail-closed lineage: format-independent. Recommending HTML here
            // would be advice to fail a second way.
            setError(LINEAGE_UNAVAILABLE_MESSAGE);
            setLineageUnavailable(true);
            return;
          }
          setError('PDF export isn’t available in this environment — try HTML.');
          return;
        }
        if (!res.ok) {
          setError(`Export failed (HTTP ${res.status}).`);
          return;
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Remediation_Action_Plan.${format}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Export failed.');
      } finally {
        setExporting(false);
      }
    },
    [authFetch, domainId],
  );

  if (acceptedCount === 0) {
    return (
      <div className="tpb-actionplan" data-testid="actionplan-bar">
        <span className="tpb-actionplan-empty">
          No plays accepted yet. Accept the plays you’ll act on to build this domain’s Remediation
          Action Plan.
        </span>
      </div>
    );
  }

  return (
    <div className="tpb-actionplan" data-testid="actionplan-bar">
      <div className="tpb-actionplan-text">
        <span className="tpb-actionplan-count" data-testid="actionplan-count">
          Remediation Action Plan: {acceptedCount} {acceptedCount === 1 ? 'play' : 'plays'}
        </span>
        <span className="tpb-actionplan-sub">
          Exactly what you accepted — no AI consolidation. Owner, effort, and timeline included.
        </span>
        {lineageUnavailable && (
          <span
            className="tpb-lineage-warning"
            role="status"
            data-testid="lineage-unavailable-warning"
          >
            {LINEAGE_UNAVAILABLE_MESSAGE}{' '}
            <button
              type="button"
              className="tpb-btn tpb-btn-inline"
              onClick={() => void probeLineage()}
            >
              Retry
            </button>
          </span>
        )}
        {error && <span className="tpb-tailor-error" role="alert">{error}</span>}
      </div>
      <div className="tpb-actionplan-actions">
        <button type="button" className="tpb-btn tpb-btn-primary" disabled={exporting} onClick={() => void exportPlan('pdf')}>
          {exporting ? 'Exporting…' : 'Export PDF'}
        </button>
        <button type="button" className="tpb-btn" disabled={exporting} onClick={() => void exportPlan('html')}>
          HTML
        </button>
      </div>
    </div>
  );
};

interface TailorBarProps {
  tailored: boolean;
  tailoredAt: string | null;
  model: string | null;
  busy: boolean;
  error: string | null;
  onTailor: () => void;
}

const TailorBar: React.FC<TailorBarProps> = ({ tailored, tailoredAt, model, busy, error, onTailor }) => (
  <div className="tpb-tailorbar" data-testid="playbook-tailorbar">
    <div className="tpb-tailorbar-text">
      {tailored ? (
        <span className="tpb-tailored-state" data-testid="tailored-state">
          <Sparkles size={14} aria-hidden="true" />
          Tailored to this domain by Bridge AI{model ? ` (${model})` : ''}
          {tailoredAt ? ` · ${new Date(tailoredAt).toLocaleDateString()}` : ''}
        </span>
      ) : (
        <span className="tpb-untailored-state">
          These are generic starting points. Tailor them to this domain’s actual scores, quotes, and
          datasets with Bridge AI before you present them.
        </span>
      )}
      {error && <span className="tpb-tailor-error" role="alert">{error}</span>}
    </div>
    <button
      type="button"
      className={tailored ? 'tpb-btn' : 'tpb-btn tpb-btn-primary'}
      disabled={busy}
      onClick={onTailor}
      data-testid="tailor-button"
    >
      <Sparkles size={14} aria-hidden="true" />
      {busy ? 'Tailoring…' : tailored ? 'Re-tailor with Bridge AI' : 'Tailor with Bridge AI'}
    </button>
  </div>
);

const PanelHeading: React.FC = () => (
  <header className="tpb-head">
    <div className="tri-eyebrow">Prescriptive</div>
    <h2 className="tpb-title">
      <ClipboardList size={20} aria-hidden="true" />
      Remediation playbook
    </h2>
  </header>
);

// ============================================================================
// Playbook card
// ============================================================================

interface CardProps {
  playbook: ResolvedPlaybook;
  /** DC-1707: active owner assignments keyed `${pattern_name}:${play_seq}`. */
  ownerByPlay: Map<string, PlayOwnerAssignment>;
  busyPlay: string | null;
  editing: string | null;
  onEdit: (key: string | null) => void;
  onSetDecision: (
    play: ResolvedPlay,
    patternName: string,
    action: PlayDecisionAction,
    edits?: { title?: string; description?: string; rationale?: string },
  ) => Promise<void>;
  onRevert: (play: ResolvedPlay, patternName: string) => Promise<void>;
}

const PlaybookCard: React.FC<CardProps> = ({
  playbook,
  ownerByPlay,
  busyPlay,
  editing,
  onEdit,
  onSetDecision,
  onRevert,
}) => {
  const fam = FAMILY_CLASS[playbook.pattern_family];
  const acceptedCount = playbook.plays.filter((p) => p.in_action_plan).length;

  return (
    <li className={`tpb-card fam-${fam}`} data-testid="playbook-card" data-pattern={playbook.pattern_name}>
      <div className="tpb-card-head">
        <div className="tpb-card-title">
          <span className={`tpb-dot fam-${fam}`} aria-hidden="true" />
          <h3>{playbook.pattern_name}</h3>
          {playbook.qualifier === 'suspected' && (
            <span className="tpb-suspected" data-testid="playbook-suspected">
              suspected
            </span>
          )}
          {playbook.qualifier === 'provisional' && (
            <span className="tpb-suspected" data-testid="playbook-provisional">
              provisional · coverage preliminary
            </span>
          )}
        </div>
        <div className="tpb-card-meta">
          {playbook.pillars_fired.map((p) => (
            <span key={p} className="tpb-pillar-chip">
              {p}
            </span>
          ))}
          <span className={`tpb-priority pr-${playbook.default_priority}`}>
            {playbook.default_priority}
          </span>
        </div>
      </div>

      <p className="tpb-firstmove">
        <span className="tpb-label">First move</span>
        {playbook.first_move}
      </p>
      <p className="tpb-owner">
        <span className="tpb-label">Owner</span>
        {playbook.owner_role}
      </p>

      {playbook.checklist.length > 0 && (
        <details className="tpb-collapse">
          <summary>Verify before acting ({playbook.checklist.length})</summary>
          <ul className="tpb-checklist">
            {playbook.checklist.map((c) => (
              <li key={c.seq}>{c.item}</li>
            ))}
          </ul>
        </details>
      )}

      <details className="tpb-collapse">
        <summary>Common misread</summary>
        <p className="tpb-misread">{playbook.common_misread}</p>
      </details>

      {(() => {
        const playsList = (
          <ol className="tpb-plays">
            {playbook.plays.map((play) => (
              <PlayRow
                key={play.seq}
                play={play}
                patternName={playbook.pattern_name}
                assignment={ownerByPlay.get(`${playbook.pattern_name}:${play.seq}`)}
                busy={busyPlay === `${playbook.pattern_name}:${play.seq}`}
                isEditing={editing === `${playbook.pattern_name}:${play.seq}`}
                onEdit={onEdit}
                onSetDecision={onSetDecision}
                onRevert={onRevert}
              />
            ))}
          </ol>
        );

        // Tailored playbook: plays are the smart, domain-specific headline → show
        // them expanded. Un-tailored: demote the generic plays behind a disclosure
        // so the canned text is never the headline (the panel CTA invites tailoring).
        if (playbook.tailored) {
          return (
            <>
              <div className="tpb-plays-label">
                Recommended plays
                {acceptedCount > 0 && (
                  <span className="tpb-inplan-count" data-testid="inplan-count">
                    {acceptedCount} in Action Plan
                  </span>
                )}
              </div>
              {playsList}
            </>
          );
        }
        return (
          <details className="tpb-collapse tpb-generic-plays" data-testid="generic-plays">
            <summary>
              Generic plays ({playbook.plays.length}) — tailor for this domain’s specifics
              {acceptedCount > 0 && (
                <span className="tpb-inplan-count" data-testid="inplan-count">
                  {acceptedCount} in Action Plan
                </span>
              )}
            </summary>
            {playsList}
          </details>
        );
      })()}
    </li>
  );
};

// ============================================================================
// Owner line (DC-1707 read-only surfacing — spec §1.2)
// ----------------------------------------------------------------------------
// Assignment upgrades, never replaces, the suggested role: unassigned plays
// keep today's `Owner: {owner_role}` render byte-for-byte; an active
// assignment promotes the line to the named owner (kind as icon + aria-label,
// never a colored badge) with up to two playsPersona names inline. The seeded
// role and any persona overflow move to the title attribute — context
// preserved, noise removed. Read-only: no assign control exists here.
// ============================================================================

const PlayOwnerLine: React.FC<{
  assignment?: PlayOwnerAssignment;
  ownerRole: string | null;
}> = ({ assignment, ownerRole }) => {
  if (!assignment) {
    return ownerRole ? <p className="tpb-play-owner">Owner: {ownerRole}</p> : null;
  }
  const personaNames = assignment.owner_personas.map((p) => p.name);
  const shown = personaNames.slice(0, 2);
  const overflow = personaNames.length - shown.length;
  const titleParts: string[] = [];
  if (overflow > 0) titleParts.push(`personas: ${personaNames.join(', ')}`);
  if (ownerRole && !personaNames.includes(ownerRole)) {
    titleParts.push(`suggested role: ${ownerRole}`);
  }
  const KindIcon = assignment.owner_kind === 'team' ? Users : User;
  return (
    <p
      className="tpb-play-owner tpb-play-owner-assigned"
      data-testid="play-owner-assigned"
      title={titleParts.length > 0 ? titleParts.join(' · ') : undefined}
    >
      <KindIcon size={13} aria-label={assignment.owner_kind} />
      Owner: <strong>{assignment.owner_display_name}</strong>
      {shown.length > 0 && (
        <span className="tpb-play-owner-personas">
          {' — '}
          {shown.join(', ')}
          {overflow > 0 ? ` +${overflow}` : ''}
        </span>
      )}
    </p>
  );
};

// ============================================================================
// Play row + decision controls
// ============================================================================

interface PlayRowProps {
  play: ResolvedPlay;
  patternName: string;
  /** DC-1707: active owner assignment for this play, when one exists. */
  assignment?: PlayOwnerAssignment;
  busy: boolean;
  isEditing: boolean;
  onEdit: (key: string | null) => void;
  onSetDecision: CardProps['onSetDecision'];
  onRevert: CardProps['onRevert'];
}

const PlayRow: React.FC<PlayRowProps> = ({
  play,
  patternName,
  assignment,
  busy,
  isEditing,
  onEdit,
  onSetDecision,
  onRevert,
}) => {
  const key = `${patternName}:${play.seq}`;
  const [draftTitle, setDraftTitle] = useState(play.title);
  const [draftDesc, setDraftDesc] = useState(play.description);
  const [draftRationale, setDraftRationale] = useState(play.rationale ?? '');

  const startEdit = (): void => {
    setDraftTitle(play.title);
    setDraftDesc(play.description);
    setDraftRationale(play.rationale ?? '');
    onEdit(key);
  };

  const stateClass =
    play.decision === 'accept'
      ? 'is-accepted'
      : play.decision === 'edit'
        ? 'is-edited'
        : play.decision === 'reject'
          ? 'is-rejected'
          : 'is-undecided';

  return (
    <li className={`tpb-play ${stateClass}`} data-testid="playbook-play" data-decision={play.decision ?? 'none'}>
      <div className="tpb-play-body">
        <div className="tpb-play-num" aria-hidden="true">
          {play.seq}
        </div>
        <div className="tpb-play-text">
          {isEditing ? (
            <div className="tpb-edit" data-testid="play-edit-form">
              <label className="tpb-edit-label">
                Title
                <input
                  type="text"
                  value={draftTitle}
                  maxLength={300}
                  onChange={(e) => setDraftTitle(e.target.value)}
                />
              </label>
              <label className="tpb-edit-label">
                Description
                <textarea
                  value={draftDesc}
                  maxLength={4000}
                  rows={3}
                  onChange={(e) => setDraftDesc(e.target.value)}
                />
              </label>
              <label className="tpb-edit-label">
                Why (optional)
                <input
                  type="text"
                  value={draftRationale}
                  maxLength={2000}
                  placeholder="Note why you tailored or accepted this play"
                  onChange={(e) => setDraftRationale(e.target.value)}
                />
              </label>
              <div className="tpb-edit-actions">
                <button
                  type="button"
                  className="tpb-btn tpb-btn-primary"
                  disabled={busy || draftTitle.trim().length === 0}
                  onClick={() =>
                    void onSetDecision(play, patternName, 'edit', {
                      title: draftTitle.trim(),
                      description: draftDesc.trim(),
                      rationale: draftRationale.trim() || undefined,
                    })
                  }
                >
                  Save &amp; accept
                </button>
                <button type="button" className="tpb-btn" disabled={busy} onClick={() => onEdit(null)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="tpb-play-title">
                {play.title}
                {play.decision === 'edit' && (
                  <span className="tpb-edited-tag" title={`Original: ${play.seed_title}`}>
                    edited
                  </span>
                )}
                {play.tailored && play.decision !== 'edit' && (
                  <span className="tpb-tailored-tag" title={`Generic: ${play.seed_title}`} data-testid="play-tailored-tag">
                    <Sparkles size={11} aria-hidden="true" />
                    AI-tailored
                  </span>
                )}
              </div>
              <p className="tpb-play-desc">{play.description}</p>
              {(play.effort || play.timeline) && (
                <div className="tpb-play-estimates">
                  {play.effort && <span className="tpb-estimate-chip">Effort: {play.effort}</span>}
                  {play.timeline && <span className="tpb-estimate-chip">{play.timeline}</span>}
                </div>
              )}
              {play.evidence_summary && (
                <p className="tpb-play-evidence">Grounded in: {play.evidence_summary}</p>
              )}
              <PlayOwnerLine assignment={assignment} ownerRole={play.owner_role} />
              {play.rationale && <p className="tpb-play-rationale">“{play.rationale}”</p>}
            </>
          )}
        </div>
      </div>

      {!isEditing && (
        <div className="tpb-play-actions" role="group" aria-label={`Decision for play ${play.seq}`}>
          {play.in_action_plan ? (
            <span className="tpb-inplan" data-testid="play-inplan">
              <Check size={13} aria-hidden="true" />
              In Action Plan
            </span>
          ) : play.decision === 'reject' ? (
            <span className="tpb-rejected-tag">Rejected</span>
          ) : null}

          {play.decision !== 'accept' && (
            <button
              type="button"
              className="tpb-btn tpb-btn-accept"
              disabled={busy}
              onClick={() => void onSetDecision(play, patternName, 'accept')}
            >
              <Check size={13} aria-hidden="true" />
              Accept
            </button>
          )}
          <button type="button" className="tpb-btn" disabled={busy} onClick={startEdit}>
            <Pencil size={13} aria-hidden="true" />
            Edit
          </button>
          {play.decision !== 'reject' && (
            <button
              type="button"
              className="tpb-btn"
              disabled={busy}
              onClick={() => void onSetDecision(play, patternName, 'reject')}
            >
              <X size={13} aria-hidden="true" />
              Reject
            </button>
          )}
          {play.decision && (
            <button
              type="button"
              className="tpb-btn tpb-btn-quiet"
              disabled={busy}
              onClick={() => void onRevert(play, patternName)}
              title="Revert to undecided"
            >
              <RotateCcw size={13} aria-hidden="true" />
              Undo
            </button>
          )}
        </div>
      )}
    </li>
  );
};

export default RemediationPlaybookPanel;
